import { Box, Button, Collapse, Typography, makeStyles, useMediaQuery } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import * as React from "react";

import { fontFamilies } from "@/designTokens";

const BUTTON_ID = "wealth-calculation-toggle";
const DETAILS_ID = "wealth-calculation-details";

const useStyles = makeStyles((theme) => ({
  button: {
    "&&&": {
      width: "100%",
      height: "auto",
      minHeight: 44,
      marginTop: 16,
      padding: "8px 12px",
      border: `1px solid ${theme.palette.semantic["border/default"]}`,
      borderRadius: 8,
      color: theme.palette.semantic["text/link"],
      fontSize: 12,
      fontWeight: 500,
      textTransform: "none",
      "&:hover": { backgroundColor: theme.palette.semantic["surface/base"] },
      "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.semantic["border/focus"]}`, outlineOffset: 2 },
    },
  },
  arrow: {
    marginLeft: 4,
    fontSize: 20,
    transition: "transform 200ms ease",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
  arrowOpen: { transform: "rotate(180deg)" },
  details: {
    marginTop: 12,
    padding: 16,
    borderRadius: 8,
    backgroundColor: theme.palette.semantic["surface/base"],
  },
  heading: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.semantic["text/heading"],
  },
  description: { marginTop: 8, fontSize: 12, lineHeight: 1.7 },
  list: { margin: "16px 0 0" },
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "4px 12px",
    padding: "10px 0",
    borderTop: `1px solid ${theme.palette.semantic["border/default"]}`,
  },
  label: { fontSize: 12, color: theme.palette.text.secondary },
  value: { margin: 0, marginLeft: "auto", fontFamily: fontFamilies.number, fontSize: 14, fontWeight: 600 },
  conditions: { marginTop: 12, fontSize: 12, lineHeight: 1.7, color: theme.palette.text.secondary },
}));

interface WealthCalculationDetailsProps {
  details: {
    principal: number;
    rent: number;
    loanPayment: number;
    monthlyCF: number;
    income6yr: number;
    capitalGain: number;
    gainRate: number;
  };
  annualLoanRate: number;
  repaymentMonths: number;
  managementFee: number;
  simulationMonths: number;
}

const formatMan = (value: number, digits = 2) =>
  value.toLocaleString("ja-JP", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const WealthCalculationDetails: React.FC<WealthCalculationDetailsProps> = ({
  details,
  annualLoanRate,
  repaymentMonths,
  managementFee,
  simulationMonths,
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = React.useState(false);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const years = simulationMonths / 12;
  const gainRatePercent = (details.gainRate * 100).toLocaleString("ja-JP", { maximumFractionDigits: 4 });
  const rows = [
    ["想定物件価格", `${formatMan(details.principal, 0)} 万円`],
    ["想定家賃", `${formatMan(details.rent)} 万円 / 月`],
    ["月々のローン返済", `${formatMan(details.loanPayment)} 万円 / 月`],
    ["管理費", `${formatMan(managementFee)} 万円 / 月`],
    ["毎月の収支", `${formatMan(details.monthlyCF)} 万円 / 月`],
    [`${years}年間の累計収支`, `${formatMan(details.income6yr)} 万円`],
    ["想定売却益", `${formatMan(details.capitalGain)} 万円`],
  ];

  return (
    <>
      <Button
        id={BUTTON_ID}
        className={classes.button}
        aria-expanded={isOpen}
        aria-controls={DETAILS_ID}
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen ? "計算方法・内訳を閉じる" : "計算方法・内訳を見る"}
        <ExpandMoreIcon className={clsx(classes.arrow, isOpen && classes.arrowOpen)} />
      </Button>
      <Collapse in={isOpen} timeout={reduceMotion ? 0 : 300} unmountOnExit>
        <Box id={DETAILS_ID} role="region" aria-labelledby={BUTTON_ID} className={classes.details}>
          <Typography component="h2" className={classes.heading}>
            計算方法
          </Typography>
          <Typography className={classes.description}>
            （{years}年間の累計収支 ＋ 想定売却益）÷ {simulationMonths}カ月で、月あたりの資産形成効果を求めています。
            毎月の収支は「家賃 − ローン返済 − 管理費」で計算しています。
          </Typography>
          <Typography className={classes.description}>
            想定売却益は、物件価格に応じた想定率（{gainRatePercent}％）を物件価格に掛けた、{years}年後の売却を想定した概算です。
            売却時の仲介手数料・税金等は含みません。
          </Typography>
          <Box component="dl" className={classes.list}>
            {rows.map(([label, value]) => (
              <div key={label} className={classes.row}>
                <Typography component="dt" className={classes.label}>
                  {label}
                </Typography>
                <Typography component="dd" className={classes.value}>
                  {value}
                </Typography>
              </div>
            ))}
          </Box>
          <Typography className={classes.conditions}>
            想定条件：金利 年{(annualLoanRate * 100).toLocaleString("ja-JP")}％・返済期間{repaymentMonths / 12}年。
            表示額は端数を丸めています。
          </Typography>
        </Box>
      </Collapse>
    </>
  );
};
